import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Newspaper, ArrowRight, Calendar, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map each Resources tab to the tag keywords that should surface a blog post.
// Matched case-insensitively against a post's `tags`.
export const TAB_TAG_MAP = {
  testing: ["wrat-5", "assessment", "testing", "evaluation"],
  tutors: ["tutor", "tutors", "tutoring", "learning styles"],
  curriculum: ["curriculum", "textbook", "textbooks", "resources"],
  coops: ["co-op", "coop", "coops", "community", "florida"],
  scholarships: ["scholarship", "scholarships", "fes-eo", "fes-ua", "step up", "funding"],
  events: ["events", "fpea", "tbhc", "field trip", "florida"],
};

const norm = (s) => (s || "").toLowerCase().trim();

export default function RelatedBlogPosts({ tabKey }) {
  const [allPosts, setAllPosts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/blog/posts`)
      .then((r) => { if (!cancelled) setAllPosts(r.data || []); })
      .catch(() => { if (!cancelled) setAllPosts([]); });
    return () => { cancelled = true; };
  }, []);

  if (!allPosts || allPosts.length === 0) return null;

  const wanted = (TAB_TAG_MAP[tabKey] || []).map(norm);
  const filtered = allPosts
    .filter((p) => (p.tags || []).some((t) => wanted.includes(norm(t))))
    .slice(0, 3);

  if (filtered.length === 0) return null;

  return (
    <section className="mt-12 pt-8 border-t border-stone-200" data-testid={`related-blog-${tabKey}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-2.5">
          <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <Newspaper className="w-4 h-4" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold">Recent from the blog</div>
            <div className="font-display font-semibold text-stone-900">Related reads</div>
          </div>
        </div>
        <Link to="/blog" className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1" data-testid={`related-blog-view-all-${tabKey}`}>
          All articles <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {filtered.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            target="_blank"
            rel="noreferrer"
            className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid={`related-blog-card-${p.slug}`}
          >
            {p.hero && <img src={p.hero} alt="" className="w-full h-32 object-cover" loading="lazy" />}
            <div className="p-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                {(p.tags || []).slice(0, 2).map((t) => (
                  <span key={t} className="rounded-full bg-amber-50 text-amber-800 text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 border border-amber-100">{t}</span>
                ))}
              </div>
              <h3 className="mt-2 font-display font-semibold text-sm leading-snug line-clamp-2 group-hover:text-amber-700 transition-colors">{p.title}</h3>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-stone-500">
                {p.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.read_mins} min</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
